import React, { useRef, useEffect, useCallback } from "react";

// You might want a different sensitivity for touch
const SCROLL_SENSITIVITY = 0.8;
const TOUCH_SENSITIVITY = 1;
const EASING = 0.08;

// INERTIA: Constants for momentum scrolling
const DAMPING = 0.95; // Higher = less friction, scrolls longer
const MOMENTUM_MULTIPLIER = 2; // Amplifies the initial velocity
const VELOCITY_TRACKER_WINDOW = 100; // ms to track velocity for

const ScrollHijackContainer = ({
	children,
	lockScrollUp = false,
	lockScrollDown = false,
	lockPoint,
	onPositionChange,
	onLockStatusChange,
	isLoading = true,
}) => {
	const containerRef = useRef(null);
	const currentPosition = useRef(0);
	const targetPosition = useRef(0);
	const animationFrameId = useRef(null);
	const maxScrollPoint = useRef(1000);

	const lastTouchPosition = useRef({ x: 0, y: 0 });
	const isTouching = useRef(false);
	const touchScrollDirection = useRef(null); // Can be 'vertical', 'horizontal', or null

	// INERTIA: Refs for momentum scrolling
	const scrollVelocity = useRef(0);
	const velocityTracker = useRef([]);

	// Store the latest values in refs to avoid stale closures
	const lockScrollUpRef = useRef(lockScrollUp);
	const lockScrollDownRef = useRef(lockScrollDown);
	const lockPointRef = useRef(lockPoint);
	const isLoadingRef = useRef(isLoading);

	useEffect(() => {
		lockScrollUpRef.current = lockScrollUp;
		lockScrollDownRef.current = lockScrollDown;
		lockPointRef.current = lockPoint;
		isLoadingRef.current = isLoading;
	}, [lockScrollUp, lockScrollDown, lockPoint, isLoading]);

	// This function will be used by both direct user input and momentum physics.
	const getClampedTarget = useCallback((intendedPosition, isScrollingUp) => {
		let newTarget = intendedPosition;

		// Apply locking logic first
		if (lockScrollUpRef.current && isScrollingUp) {
			newTarget = Math.max(newTarget, lockPointRef.current);
		} else if (lockScrollDownRef.current && !isScrollingUp) {
			newTarget = Math.min(newTarget, lockPointRef.current);
		}

		// Then, apply the absolute boundaries of the scroll area
		newTarget = Math.max(0, newTarget);
		newTarget = Math.min(newTarget, maxScrollPoint.current);

		return newTarget;
	}, []); // This function only uses refs, so it doesn't need dependencies.

	// INERTIA: The animation loop now handles both easing and momentum
	const animationLoop = useCallback(() => {
		if (isLoadingRef.current) {
			targetPosition.current = currentPosition.current;
			scrollVelocity.current = 0;
		}

		// Apply momentum if not touching and there's velocity
		if (!isTouching.current && Math.abs(scrollVelocity.current) > 0.01) {
			const isScrollingUp = scrollVelocity.current < 0;
			const intendedPosition =
				targetPosition.current + scrollVelocity.current;

			// Apply the locking/clamping logic to the momentum scroll
			const newTarget = getClampedTarget(intendedPosition, isScrollingUp);

			// If the lock or a boundary stopped the momentum, kill the velocity
			if (newTarget === targetPosition.current) {
				scrollVelocity.current = 0;
			}

			targetPosition.current = newTarget; // Update the target position based on momentum
			scrollVelocity.current *= DAMPING; // Apply damping (friction)
		}

		// Lerp (linear interpolation) for the smooth scrolling effect

		const newPosition =
			currentPosition.current +
			(targetPosition.current - currentPosition.current) * EASING;
		currentPosition.current = newPosition;

		if (containerRef.current) {
			containerRef.current.style.transform = `translateY(-${currentPosition.current.toFixed(
				2
			)}px)`;
		}

		if (onPositionChange) {
			onPositionChange(currentPosition.current);
		}

		// Stop the loop if the scroll is settled and there's no more momentum
		const isSettled =
			Math.abs(targetPosition.current - currentPosition.current) < 0.5;
		const hasMomentum = Math.abs(scrollVelocity.current) > 0.01;

		if (isSettled && !hasMomentum) {
			currentPosition.current = targetPosition.current;
			if (containerRef.current) {
				containerRef.current.style.transform = `translateY(-${currentPosition.current.toFixed(
					2
				)}px)`;
			}
			if (onPositionChange) {
				onPositionChange(currentPosition.current);
			}
			animationFrameId.current = null;
		} else {
			animationFrameId.current = requestAnimationFrame(animationLoop);
		}
	}, [onPositionChange, getClampedTarget]);

	const startAnimation = useCallback(() => {
		if (!animationFrameId.current) {
			animationFrameId.current = requestAnimationFrame(animationLoop);
		}
	}, [animationLoop]);

	const updateTargetPosition = useCallback(
		(delta, isScrollingUp) => {
			const intendedScroll = targetPosition.current + delta;
			const newTarget = getClampedTarget(intendedScroll, isScrollingUp);
			targetPosition.current = newTarget;

			if (onLockStatusChange) {
				const isLocked =
					Math.abs(lockPointRef.current - newTarget) < 0.01;
				onLockStatusChange(isLocked);
			}
			startAnimation();
		},
		[startAnimation, onLockStatusChange, getClampedTarget]
	);

	// MODIFIED: The Wheel handler
	const handleWheel = useCallback(
		(e) => {
			// Check if the scroll is primarily horizontal.
			const isHorizontalScroll =
				Math.abs(e.deltaX) + 2 > Math.abs(e.deltaY); // This handles horizontal trackpad scrolling. (+2 makes it to be more sensitive to horizontal scroll)
			if (e.shiftKey || isHorizontalScroll) {
				// It's horizontal, do nothing and let the browser handle it.
				return;
			}

			// If we are here, it's a vertical scroll. Proceed with hijacking.
			e.preventDefault();
			if (isLoadingRef.current) return;
			const delta = e.deltaY * SCROLL_SENSITIVITY;
			const isScrollingUp = e.deltaY < 0;
			scrollVelocity.current = 0;
			updateTargetPosition(delta, isScrollingUp);
		},
		[updateTargetPosition]
	);

	const handleTouchStart = useCallback(
		(e) => {
			if (isLoadingRef.current) return;
			scrollVelocity.current = 0;
			isTouching.current = true;
			lastTouchPosition.current = {
				x: e.touches[0].clientX,
				y: e.touches[0].clientY,
			};
			touchScrollDirection.current = null;
			velocityTracker.current = [];
			startAnimation();
		},
		[startAnimation]
	);

	const handleTouchMove = useCallback(
		(e) => {
			if (!isTouching.current) return;

			const clientX = e.touches[0].clientX;
			const clientY = e.touches[0].clientY;

			const deltaX = lastTouchPosition.current.x - clientX;
			const deltaY = lastTouchPosition.current.y - clientY;

			// NEW: Determine scroll direction on the first significant move
			if (touchScrollDirection.current === null) {
				// Use a small threshold to avoid locking direction on a small tap jitter
				if (Math.abs(deltaY) > 5 || Math.abs(deltaX) > 5) {
					touchScrollDirection.current =
						Math.abs(deltaY) > Math.abs(deltaX)
							? "vertical"
							: "horizontal";
				}
			}

			// Only hijack vertical scrolls
			if (touchScrollDirection.current === "vertical") {
				e.preventDefault(); // Only prevent default for vertical scrolls

				if (isLoadingRef.current) {
					lastTouchPosition.current = { x: clientX, y: clientY };
					return;
				}

				const verticalDelta = deltaY * TOUCH_SENSITIVITY;
				const isScrollingUp = verticalDelta < 0;

				const now = Date.now();
				velocityTracker.current.push({
					delta: verticalDelta,
					time: now,
				});
				velocityTracker.current = velocityTracker.current.filter(
					(entry) => now - entry.time < VELOCITY_TRACKER_WINDOW
				);

				lastTouchPosition.current = { x: clientX, y: clientY };
				updateTargetPosition(verticalDelta, isScrollingUp);
			}
			// If direction is 'horizontal' or still null, do nothing and allow native browser scroll.
		},
		[updateTargetPosition]
	);

	// MODIFIED: The TouchEnd handler
	const handleTouchEnd = useCallback(() => {
		isTouching.current = false;

		// NEW: Only apply inertia if we were scrolling vertically
		if (touchScrollDirection.current === "vertical") {
			const now = Date.now();
			const recentMoves = velocityTracker.current.filter(
				(entry) => now - entry.time < VELOCITY_TRACKER_WINDOW
			);

			if (recentMoves.length > 1) {
				const totalDelta = recentMoves.reduce(
					(acc, entry) => acc + entry.delta,
					0
				);
				const averageDelta = totalDelta / recentMoves.length;
				scrollVelocity.current = averageDelta * MOMENTUM_MULTIPLIER;
			}
			startAnimation();
		}

		// NEW: Reset the direction for the next touch
		touchScrollDirection.current = null;
	}, [startAnimation]);

	useEffect(() => {
		if (window.history.scrollRestoration) {
			window.history.scrollRestoration = "manual";
		}
		document.body.style.overflow = "hidden";

		window.smoothScrollTo = (y) => {
			if (isLoadingRef.current) return;
			// Determine scroll direction based on where we are and where we're going.
			const isScrollingUp = y < currentPosition.current;

			// Use the existing clamping and locking logic to determine the final destination.
			const finalTarget = getClampedTarget(y, isScrollingUp);

			if (finalTarget === lockPointRef.current) {
				onLockStatusChange?.(true);
			}

			targetPosition.current = finalTarget;
			scrollVelocity.current = 0; // Stop any existing momentum
			startAnimation();
		};

		window.addEventListener("wheel", handleWheel, { passive: false });
		window.addEventListener("touchstart", handleTouchStart, {
			passive: false,
		});
		window.addEventListener("touchmove", handleTouchMove, {
			passive: false,
		});
		window.addEventListener("touchend", handleTouchEnd);

		let resizeTimeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(calculateMaxScrollPoint, 100);
		};

		const calculateMaxScrollPoint = () => {
			maxScrollPoint.current =
				document.body.scrollHeight - window.innerHeight;
			if (targetPosition.current > maxScrollPoint.current) {
				targetPosition.current = maxScrollPoint.current;
				startAnimation();
			}

			document.body.style.overflow = "hidden";

			// document scroll should always set to 0
			document.documentElement.scrollTop = 0;
			document.body.scrollTop = 0;
		};
		const maxScrollPointInterval = setInterval(
			calculateMaxScrollPoint,
			500
		);
		setTimeout(calculateMaxScrollPoint, 100);
		window.addEventListener("resize", handleResize);

		const preventGesture = (e) => {
			e.preventDefault();
		};

		document.addEventListener("gesturestart", preventGesture);
		document.addEventListener("gesturechange", preventGesture);
		document.addEventListener("gestureend", preventGesture);

		return () => {
			document.body.style.overflow = "auto";
			delete window.smoothScrollTo;
			window.removeEventListener("wheel", handleWheel);
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("touchstart", handleTouchStart);
			window.removeEventListener("touchmove", handleTouchMove);
			window.removeEventListener("touchend", handleTouchEnd);

			document.removeEventListener("gesturestart", preventGesture);
			document.removeEventListener("gesturechange", preventGesture);
			document.removeEventListener("gestureend", preventGesture);

			clearInterval(maxScrollPointInterval);
			clearTimeout(resizeTimeout);
			if (animationFrameId.current) {
				cancelAnimationFrame(animationFrameId.current);
			}
		};
	}, [
		handleWheel,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
		startAnimation,
		getClampedTarget,
		onLockStatusChange,
	]);

	return (
		<div ref={containerRef} className="scroll-hijack-container">
			{children}
		</div>
	);
};

export default ScrollHijackContainer;
